/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { urls } from '@/Constants';
import serverChatServices from '@/Services/server-chat.services';
import MarkdownView from './MarkdownView';
import styles from './ServerChatGuide.module.scss';

// Permanent "How Server Chat works" page: /internal/server-chat/guide (#section for a deep link).
// The text lives in the backend (chatbot_template/GUIDE.md + HOW_IT_WORKS.md), so it updates with a deploy.
const TABS = [['guide', 'Guide'], ['technical', 'Technical details']];

export function guideHref(section) {
	return `${window.location.origin}${urls.EXTENSION}${urls.SERVER_CHAT}/guide${section ? `#${section}` : ''}`;
}

const slug = (t) => t.toLowerCase().replace(/[`*]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// "# Title\nintro\n## A\n...\n## B\n..." -> {title, intro, sections: [{id, title, body}]}
function splitDoc(md) {
	const lines = (md || '').replace(/\r\n/g, '\n').split('\n');
	let title = '';
	const intro = [];
	const sections = [];
	let cur = null;
	let inCode = false;
	for (const line of lines) {
		if (line.startsWith('```')) inCode = !inCode;
		if (!inCode && !title && /^# /.test(line)) { title = line.slice(2).trim(); continue; }
		if (!inCode && /^## /.test(line)) {
			const t = line.slice(3).trim();
			cur = { id: slug(t), title: t, body: [] };
			sections.push(cur);
			continue;
		}
		if (!inCode && /^---\s*$/.test(line)) continue;   // the section cards already separate things
		(cur ? cur.body : intro).push(line);
	}
	return { title, intro: intro.join('\n').trim(), sections: sections.map((s) => ({ ...s, body: s.body.join('\n').trim() })) };
}

export default function ServerChatGuide() {
	const history = useHistory();
	const location = useLocation();
	const [docs, setDocs] = useState(null);
	const [error, setError] = useState('');
	const [tab, setTab] = useState(() => (new URLSearchParams(location.search).get('tab') === 'technical' ? 'technical' : 'guide'));
	const [active, setActive] = useState('');
	const [copied, setCopied] = useState('');
	const bodyRef = useRef(null);

	useEffect(() => {
		serverChatServices.chatAction('guide', {})
			.then((res) => (res?.data ? setDocs(res.data) : setError('The guide could not be loaded.')))
			.catch((e) => setError(String(e || 'The guide could not be loaded.')));
	}, []);

	const doc = useMemo(() => splitDoc(docs?.[tab]), [docs, tab]);

	// Deep link (#section) after load, and highlight the section being read in the contents list.
	useEffect(() => {
		if (!doc.sections.length) return undefined;
		const id = decodeURIComponent((location.hash || '').slice(1));
		if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), 0);
		const root = bodyRef.current;
		const obs = new IntersectionObserver((entries) => {
			const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
			if (vis[0]) setActive(vis[0].target.id);
		}, { root, rootMargin: '0px 0px -70% 0px' });
		doc.sections.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
		return () => obs.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [doc]);

	const switchTab = (k) => {
		setTab(k);
		setActive('');
		history.replace({ search: k === 'technical' ? '?tab=technical' : '', hash: '' });
		bodyRef.current?.scrollTo({ top: 0 });
	};

	const go = (id) => {
		document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		history.replace({ search: location.search, hash: id });
	};

	const copyLink = (id) => {
		const href = `${guideHref(id)}`.replace('/guide#', `/guide${tab === 'technical' ? '?tab=technical' : ''}#`);
		try { navigator.clipboard.writeText(href); } catch (e) { /* old browser */ }
		setCopied(id);
		setTimeout(() => setCopied(''), 1500);
	};

	return (
		<div className={styles.page}>
			<header className={styles.top}>
				<button type='button' className={styles.back} onClick={() => history.push(`${urls.EXTENSION}${urls.SERVER_CHAT}`)}>
					<ArrowBackRoundedIcon fontSize='small' aria-hidden /> Server Chat
				</button>
				<div className={styles.tabs} role='tablist' aria-label='Guide sections'>
					{TABS.map(([k, label]) => (
						<button key={k} type='button' role='tab' aria-selected={tab === k}
							className={tab === k ? styles.tabOn : ''} onClick={() => switchTab(k)}>{label}</button>
					))}
				</div>
			</header>

			<div className={styles.layout}>
				<nav className={styles.toc} aria-label='Contents'>
					<div className={styles.tocLabel}>On this page</div>
					{doc.sections.map((s) => (
						<button key={s.id} type='button' className={`${styles.tocItem} ${active === s.id ? styles.tocOn : ''}`}
							onClick={() => go(s.id)}>{s.title.replace(/^\d+\.\s*/, '')}</button>
					))}
				</nav>

				<main className={styles.body} ref={bodyRef}>
					<div className={styles.inner}>
						{error && <div className={styles.error}>{error}</div>}
						{!docs && !error && <div className={styles.loading}>Loading the guide…</div>}
						{docs && (
							<>
								<div className={styles.hero}>
									<div className={styles.heroIcon}><MenuBookRoundedIcon fontSize='inherit' aria-hidden /></div>
									<div>
										<div className={styles.kicker}>{tab === 'technical' ? 'For developers' : 'Server Chat'}</div>
										<h1>{doc.title || 'How Server Chat works'}</h1>
									</div>
								</div>
								{!!doc.intro && <div className={styles.intro}><MarkdownView text={doc.intro} /></div>}
								{doc.sections.map((s) => (
									<section key={s.id} id={s.id} className={styles.section}>
										<h2>
											{s.title}
											<button type='button' className={styles.anchor} onClick={() => copyLink(s.id)}
												title='Copy a link to this section' aria-label={`Copy a link to ${s.title}`}>
												{copied === s.id ? <CheckRoundedIcon fontSize='inherit' /> : <LinkRoundedIcon fontSize='inherit' />}
											</button>
										</h2>
										<MarkdownView text={s.body} />
									</section>
								))}
								{!doc.sections.length && <MarkdownView text={docs[tab] || 'Not available on this server yet.'} />}
							</>
						)}
					</div>
				</main>
			</div>
		</div>
	);
}
